import type { QueryClient } from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import { cellCommentThreadsKey } from "@/payroll/ai-script/cell-comments/query";
import { clearSavedManualRowEdits } from "@/payroll/ai-script/spreadsheet-cache/pending-manual-rows";
import {
  aiScriptSpreadsheetSheetKey,
  aiScriptSpreadsheetSheetListKey,
  refreshChangedSpreadsheetSheets,
  startChangedSpreadsheetSheetWarmup,
} from "@/payroll/ai-script/spreadsheet-sheet-query";
import type {
  AcceptedAiSpreadsheetOperationResponse,
  AiSpreadsheetOperationResult,
  CachedSpreadsheetSheetResponse,
} from "@/payroll/ai-script/types";
import {
  flushWorkbookCellEdits,
  hasPendingWorkbookCellEdits,
} from "@/payroll/ai-script/workbook-cell-edit-batcher";
import {
  type WorkbookMutationRefreshContext,
  type WorkbookRefreshMode,
  executeQueuedWorkbookMutation,
  isRecoverableWorkbookOperationError,
} from "@/payroll/ai-script/workbook-mutation";
import { payPeriodDetailPrefixKey } from "@/payroll/pay-periods/query";
import {
  aiScriptCellOverrideHistoryPrefixKey,
  aiScriptCellOverridesKey,
  aiScriptManualRowsKey,
  aiScriptSpreadsheetKey,
  aiScriptStatementPrefixKey,
} from "./keys";

export const clearSavedManualRowCellDrafts = (
  queryClient: QueryClient,
  businessId: string,
  runId: string,
  sheetId: string,
  cellIds: string[],
) => {
  const savedIds = new Set(cellIds);
  queryClient.setQueryData<CachedSpreadsheetSheetResponse>(
    aiScriptSpreadsheetSheetKey(businessId, runId, sheetId),
    (data) => clearSavedManualRowEdits(data, savedIds),
  );
};

export const invalidateOptimisticSheetOnMutationError = (
  queryClient: QueryClient,
  error: unknown,
  context:
    | {
        sheetKey?: ReturnType<typeof aiScriptSpreadsheetSheetKey>;
      }
    | undefined,
) => {
  if (isRecoverableWorkbookOperationError(error) || !context?.sheetKey) return;

  void queryClient.invalidateQueries({ queryKey: context.sheetKey });
};

export const derivedWorkbookViewKeys = (businessId: string, runId: string) => [
  aiScriptCellOverridesKey(businessId, runId),
  aiScriptCellOverrideHistoryPrefixKey(businessId, runId),
  aiScriptManualRowsKey(businessId, runId),
  aiScriptStatementPrefixKey(businessId, runId),
  payPeriodDetailPrefixKey(),
];

export const refreshWorkbookAfterOperation = async (
  queryClient: QueryClient,
  businessId: string,
  runId: string,
) => {
  await Promise.all(
    [
      aiScriptSpreadsheetKey(businessId, runId),
      aiScriptSpreadsheetSheetListKey(businessId, runId),
      ...derivedWorkbookViewKeys(businessId, runId),
      cellCommentThreadsKey(businessId, runId),
    ].map((queryKey) => queryClient.invalidateQueries({ queryKey })),
  );
};

export const refreshWorkbookAfterCellEdits = async (
  queryClient: QueryClient,
  businessId: string,
  runId: string,
  { immediateSheetId, changedSheetIds }: WorkbookMutationRefreshContext,
) => {
  await refreshChangedSpreadsheetSheets({
    queryClient,
    businessId,
    runId,
    changedSheetIds,
    immediateSheetId,
  });

  for (const queryKey of derivedWorkbookViewKeys(businessId, runId)) {
    void queryClient.invalidateQueries({ queryKey });
  }
};

export const executeWorkbookMutation = async ({
  api,
  queryClient,
  businessId,
  runId,
  submit,
  refreshMode = "full",
  immediateSheetId,
  flushPendingCellEdits = true,
  hasPendingClientBatch,
  onApplied,
}: {
  api: AxiosInstance;
  queryClient: QueryClient;
  businessId: string;
  runId: string;
  submit: (
    idempotencyKey: string,
  ) => Promise<AcceptedAiSpreadsheetOperationResponse>;
  refreshMode?: WorkbookRefreshMode;
  immediateSheetId?: string;
  flushPendingCellEdits?: boolean;
  hasPendingClientBatch?: () => boolean;
  onApplied?: (result: AiSpreadsheetOperationResult) => void;
}) => {
  if (flushPendingCellEdits) {
    await flushWorkbookCellEdits(businessId, runId);
  }

  return executeQueuedWorkbookMutation({
    api,
    businessId,
    runId,
    submit,
    refreshMode,
    immediateSheetId,
    hasPendingClientBatch:
      hasPendingClientBatch ??
      (() => hasPendingWorkbookCellEdits(businessId, runId)),
    onApplied,
    onQueueDrained: async (context) => {
      if (!context.requiresFullRefresh) {
        await refreshWorkbookAfterCellEdits(
          queryClient,
          businessId,
          runId,
          context,
        );
        return;
      }

      await refreshWorkbookAfterOperation(queryClient, businessId, runId);
      void startChangedSpreadsheetSheetWarmup({
        api,
        queryClient,
        businessId,
        runId,
        sheetIds: context.changedSheetIds,
      });
    },
  });
};
